import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize, tap } from 'rxjs/operators';

import { AuthenticationService } from 'app/core/authentication';
import { QuestionAnswerType } from 'app/question/constants/question-answer-type';
import { QuestionChoiceWithSelectionIndicatorModel } from 'app/question/models/question-choice-with-selection-indicator.model';
import { isImageSwap, replaceFISChoice } from 'app/question/utils/question-utils';
import { ManipRenderingService } from 'app/shared/services/manip-rendering.service';
import { StudentQuestionAttemptModel } from 'app/student-question-attempt/student-question-attempt.model';
import { StudentQuestionAttemptService } from 'app/student-question-attempt/student-question-attempt.service';

@UntilDestroy()
@Component({
  selector: 'kh-student-question-attempt-content',
  templateUrl: './student-question-attempt-content.component.html',
  styleUrls: ['./student-question-attempt-content.component.less'],
  standalone: false
})
export class StudentQuestionAttemptContentComponent implements OnInit {
  private readonly studentQuestionAttemptService = inject(StudentQuestionAttemptService);
  private readonly manipRenderingService = inject(ManipRenderingService);
  private readonly authService = inject(AuthenticationService);

  @Input() public attemptId = '';
  @Input() public itemOrder = 0;
  @Input() public isTeacher = true;
  @Input() public hideActions = false;
  @Input() public isDemo = false;
  @Input() public isModal = true;
  @Input() public isSingleChoice = false;
  @Input() public hideName = false;

  @Output() public closed = new EventEmitter<void>();

  public question?: StudentQuestionAttemptModel;
  public isLoading = false;
  public imagePath: string | null = null;

  // Hide extras for non-teachers (for PLC)
  // TODO: Need to be removed after PLC will be removed
  public hideExtras(): boolean {
    return !this.isTeacher;
  }

  public canHideChoice(questionChoice: QuestionChoiceWithSelectionIndicatorModel): boolean {
    return (
      !questionChoice.IsFirstSelection
      && !questionChoice.IsSecondSelection
      && this.question?.AnswerType === QuestionAnswerType.OpenAnswer
      && !questionChoice.IsCorrect
    ) || (!questionChoice.IsFirstSelection && !questionChoice.IsSecondSelection && !!this.question?.IsSurveyQuestion);
  }

  public ngOnInit(): void {
    this.imagePath = this.authService.userId();
    this.isLoading = true;

    this.studentQuestionAttemptService.getQuestionAttempt(this.attemptId, this.itemOrder).pipe(
      tap((questionAttempt) => {
        const attempt = questionAttempt as StudentQuestionAttemptModel;
        attempt.FreeFormAnswers = questionAttempt.QuestionChoices.filter(({ IsFirstSelection }) => IsFirstSelection).map(({ AnswerText }) => AnswerText);
        attempt.AttemptId = this.attemptId;
        attempt.AttemptItemOrder = this.itemOrder;
        this.manipRenderingService.renderDraggableQuestion(attempt);

        this.question = attempt;

        this.question.QuestionChoices.forEach((choice) => {
          if (isImageSwap(this.question!.AnswerType, this.question!.ManipulativeType)) {
            choice.RenderQuestion = replaceFISChoice(choice.RenderQuestion);
          }
        });
      }),
      finalize(() => this.isLoading = false),
      untilDestroyed(this)
    ).subscribe();
  }

  public canHideChoiceSideIcon(choice: QuestionChoiceWithSelectionIndicatorModel): boolean {
    return (!choice.IsCorrect && !choice.IsFirstSelection && !choice.IsSecondSelection) || this.question!.IsSurveyQuestion;
  }

  public closeModal(): void {
    this.closed.emit();
  }
}
